/**
 * 应用外框：顶栏（地平线、命令入口、后台状态）+ 左侧导航 + 主区 + 导师（阅读助手）。
 * 宽屏（≥1280）导师常驻右栏；较窄时导师是右侧抽屉；手机用底部标签栏，导师全屏。
 * 阅读页占满主区（阅读器自己翻页 / 滚动）。
 */
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import clsx from "clsx";
import { BookOpen, Library, MessageCircle, Search, Settings } from "lucide-react";
import { lazy, type ReactNode, Suspense, useEffect, useState } from "react";
import { UpdateBanner } from "@/features/devices/AppUpdate";
import { FeedbackButton } from "@/features/devices/FeedbackButton";
import { KbRequests } from "@/features/devices/KbAgents";
import { PairingRequests } from "@/features/devices/PairingRequests";
import { PairScreen } from "@/features/devices/PairScreen";
import { pairing } from "@/shared/api/client";
import { useApprovals, useJobs, useModels, useSources } from "@/shared/api/hooks";
import { inApp } from "@/shared/native";
import { useMedia, useUi, WIDE } from "@/shared/scene";
import { Spinner } from "@/shared/ui";
import { CommandPalette } from "./CommandPalette";

// 导师面板（含流式 Markdown、公式与对话协议）按需加载，首屏更快
const LazyTutor = lazy(() => import("@/features/tutor/TutorPanel").then((m) => ({ default: m.TutorPanel })));

function TutorPanel({ onClose }: { onClose?: () => void }) {
  return (
    <Suspense
      fallback={
        <div className="grid h-full place-items-center bg-surface text-text-3">
          <Spinner />
        </div>
      }
    >
      <LazyTutor onClose={onClose} />
    </Suspense>
  );
}

type NavItem = { to: string; label: string; icon: ReactNode };

/** 未配对的设备（经 Tailscale 来的手机）先配对，配好才显示应用。 */
export function AppShell() {
  const [unpaired, setUnpaired] = useState(pairing.unpaired);
  useEffect(() => {
    const fn = () => setUnpaired(true);
    pairing.listeners.add(fn);
    return () => {
      pairing.listeners.delete(fn);
    };
  }, []);
  return unpaired ? <PairScreen /> : <Shell />;
}

function Shell() {
  const theme = useUi((s) => s.theme);
  const tutorOpen = useUi((s) => s.tutorOpen);
  const setTutorOpen = useUi((s) => s.setTutorOpen);
  const drawerOpen = useUi((s) => s.drawerOpen);
  const setDrawerOpen = useUi((s) => s.setDrawerOpen);
  const wide = useMedia(WIDE);
  const setPaletteOpen = useUi((s) => s.setPaletteOpen);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const reading = pathname.startsWith("/read/");
  // 手机上读书时沉浸：不显示应用顶栏与底部标签，点正文中间再呼出阅读菜单
  const phone = useMedia("(max-width: 767px)") || inApp();
  const immersive = reading && phone;

  useEffect(() => {
    if (theme === "system") delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setPaletteOpen]);

  const nav: NavItem[] = [{ to: "/", label: "书库", icon: <Library className="size-4" /> }];
  const onTutorPage = pathname === "/tutor";

  return (
    <div className="flex h-full flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-surface focus:p-2"
      >
        跳到正文
      </a>
      {immersive ? null : (
        <TopBar onOpenTutor={() => (wide ? setTutorOpen(!tutorOpen) : setDrawerOpen(true))} />
      )}
      <PairingRequests />
      <KbRequests />
      <UpdateBanner />
      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="主导航"
          className={clsx(
            "hidden w-[180px] shrink-0 flex-col border-r border-border px-3 py-4",
            reading ? "xl:flex" : "md:flex",
          )}
        >
          <ul className="flex flex-col gap-0.5">
            {nav.map((item) => (
              <li key={item.to}>
                <NavLink item={item} />
              </li>
            ))}
          </ul>
          <div className="mt-auto">
            <NavLink item={{ to: "/settings", label: "设置", icon: <Settings className="size-4" /> }} />
          </div>
        </nav>
        <main
          id="main"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: 主区是独立滚动容器，键盘需要能聚焦后滚动（WCAG 2.1.1）
          tabIndex={0}
          className={clsx(
            "min-w-0 flex-1 overflow-x-hidden",
            immersive
              ? "flex flex-col overflow-y-hidden"
              : reading
                ? "flex flex-col overflow-y-hidden pb-14 md:pb-0"
                : "overflow-y-auto pb-20 md:pb-6",
          )}
        >
          <Outlet />
        </main>
        {/* 同一时刻只挂载一个导师面板：宽屏常驻右栏，窄屏是右侧抽屉；手机上的“导师”标签是整页 */}
        {!onTutorPage && wide && tutorOpen ? (
          <div className="w-[380px] shrink-0 border-l border-border">
            <TutorPanel onClose={() => setTutorOpen(false)} />
          </div>
        ) : null}
        {!onTutorPage && !wide && drawerOpen ? (
          <div className="fixed inset-0 z-40">
            <button
              type="button"
              aria-label="关闭导师"
              className="absolute inset-0 bg-text/20"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="absolute inset-y-0 right-0 flex w-full max-w-[420px] flex-col border-l border-border shadow-soft">
              <TutorPanel onClose={() => setDrawerOpen(false)} />
            </div>
          </div>
        ) : null}
      </div>
      {immersive ? null : <BottomTabs />}
      <CommandPalette />
    </div>
  );
}

function NavLink({ item }: { item: NavItem }) {
  return (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === "/" }}
      className="flex h-9 items-center gap-2.5 rounded-s px-3 text-[13.5px] text-text-2 hover:bg-surface-2 hover:text-text data-[status=active]:bg-nav-active data-[status=active]:font-semibold data-[status=active]:text-text"
    >
      {item.icon}
      {item.label}
    </Link>
  );
}

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 font-bold tracking-[0.15em]" aria-label="朝夕 首页">
      <span
        aria-hidden
        className="size-6 rounded-full bg-[image:var(--horizon)] shadow-[inset_0_-8px_0_var(--action)]"
      />
      <span className="hidden sm:inline">朝夕</span>
    </Link>
  );
}

function TopBar({ onOpenTutor }: { onOpenTutor: () => void }) {
  const setPaletteOpen = useUi((s) => s.setPaletteOpen);
  return (
    <header className="horizon relative flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-4">
      <Logo />
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-m bg-surface-2 px-3 text-left text-[13px] text-text-3 md:max-w-[460px]"
      >
        <Search className="size-4 shrink-0" />
        <span className="truncate">搜索页面与能力，或输入命令</span>
        <kbd className="ml-auto hidden rounded border border-border px-1.5 font-mono text-[11px] sm:inline">
          ⌘K
        </kbd>
      </button>
      <div className="ml-auto flex items-center gap-2">
        <FeedbackButton />
        <JobsPill />
        <ApprovalsPill />
        <ModelPill />
        <button
          type="button"
          onClick={onOpenTutor}
          className="flex h-8 items-center gap-1.5 rounded-full border border-border px-3 text-[12.5px] text-text-2 hover:text-text max-md:hidden"
        >
          <MessageCircle className="size-3.5" />
          导师
        </button>
      </div>
    </header>
  );
}

function JobsPill() {
  const { data } = useJobs();
  const running = (data ?? []).filter((j) => j.status === "queued" || j.status === "running");
  const job = running[0];
  if (!job) return null;
  const progress = job.progress as { done?: number; total?: number };
  return (
    <span
      role="status"
      className="hidden h-8 items-center gap-2 rounded-full bg-surface-2 px-3 text-[12.5px] text-text-2 lg:flex"
      title={job.stage || job.title}
    >
      <span className="h-1 w-8 overflow-hidden rounded-full bg-border">
        <span
          className="block h-full animate-pulse bg-signal"
          style={{ width: progress.total ? `${((progress.done ?? 0) / progress.total) * 100}%` : "40%" }}
        />
      </span>
      {job.title}
      {running.length > 1 ? ` 等 ${running.length} 项` : ""}
    </span>
  );
}

function ApprovalsPill() {
  const { data } = useApprovals();
  const showTutor = useUi((s) => s.showTutor);
  const pending = (data ?? []).filter((a) => a.status === "pending").length;
  if (!pending) return null;
  return (
    <button
      type="button"
      onClick={showTutor}
      className="flex h-8 items-center gap-1.5 rounded-full bg-signal-soft px-3 text-[12.5px] font-semibold text-signal-ink"
    >
      <span aria-hidden className="size-1.5 rounded-full bg-signal" />
      等确认 {pending}
    </button>
  );
}

function ModelPill() {
  const { data } = useModels();
  const tutor = data?.routes.find((r) => r.role === "tutor");
  if (!tutor) return null;
  const demo = tutor.provider === "demo";
  return (
    <Link
      to="/settings"
      className={clsx(
        "hidden h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] lg:flex",
        tutor.ready ? "bg-surface-2 text-text-2" : "bg-warn-soft text-warn",
      )}
      title={tutor.reason || undefined}
    >
      {demo ? "演示模型" : tutor.ready ? `${tutor.subscription ? "订阅 · " : ""}${tutor.model}` : "导师未就绪"}
    </Link>
  );
}

function BottomTabs() {
  const books = useSources("active");
  const last = [...(books.data ?? [])]
    .filter((b) => b.last_read_at)
    .sort((a, b) => (b.last_read_at ?? "").localeCompare(a.last_read_at ?? ""))[0];
  const tab =
    "flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] text-text-3 data-[status=active]:font-semibold data-[status=active]:text-signal-ink";
  return (
    <nav
      aria-label="底部导航"
      className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <Link to="/" activeOptions={{ exact: true }} className={tab}>
        <Library className="size-5" />
        书库
      </Link>
      {last ? (
        <Link to="/read/$sourceId" params={{ sourceId: last.id }} className={tab}>
          <BookOpen className="size-5" />
          接着读
        </Link>
      ) : (
        <span className={clsx(tab, "opacity-50")}>
          <BookOpen className="size-5" />
          接着读
        </span>
      )}
      <Link to="/tutor" className={tab}>
        <MessageCircle className="size-5" />
        导师
      </Link>
      <Link to="/settings" className={tab}>
        <Settings className="size-5" />
        设置
      </Link>
    </nav>
  );
}
